import { SubRipText } from "@leawind/srt-parser";
import { parseTimeToMs, SrtNode } from "@leawind/srt-parser/internal";

/**
 * SRTファイルのテキストをパースしてノードの配列に変換
 */
export function parseSrtFile(content: string): SrtNode[] {
  const srt = SubRipText.fromText(content.replace(/\r\n/g, "\n"));
  return srt.nodes;
}

/**
 * ノードの配列をSRT形式のテキストに変換
 */
export function stringifySrtNodes(nodes: SrtNode[]): string {
  const srt = new SubRipText();
  srt.nodes.push(...nodes);
  return srt.toString();
}

/**
 * エントリの時間を更新
 */
export function updateEntryTime(
  entries: SrtNode[],
  id: number,
  field: "appear" | "disappear",
  value: string,
): SrtNode[] {
  return entries.map((entry) => {
    if (entry.id === id) {
      entry[field] = parseTimeToMs(value);
    }
    return entry;
  });
}

/**
 * エントリのテキストを更新
 */
export function updateEntryText(
  entries: SrtNode[],
  id: number,
  value: string,
): SrtNode[] {
  return entries.map((entry) => {
    if (entry.id === id) {
      entry.subtitle = value;
    }
    return entry;
  });
}

/**
 * エントリのIDを1から順に振り直す
 */
function renumberEntries(entries: SrtNode[]): SrtNode[] {
  return entries.map((entry, index) => {
    entry.id = index + 1;
    return entry;
  });
}

/**
 * エントリを削除
 */
export function deleteEntry(entries: SrtNode[], id: number): SrtNode[] {
  const filtered = entries.filter((entry) => entry.id !== id);
  return renumberEntries(filtered);
}

/**
 * 新しいエントリを追加
 */
export function addNewEntry(entries: SrtNode[]): SrtNode[] {
  const newId = entries.length > 0
    ? entries.reduce((max, e) => Math.max(max, e.id), 0) + 1
    : 1;
  return [
    ...entries,
    new SrtNode(newId, 0, 0, ""),
  ];
}

/**
 * エントリを2つに分割（複数行の場合、行数や文字数に応じて分割）
 */
export function splitEntry(entries: SrtNode[], id: number): SrtNode[] {
  const entryIndex = entries.findIndex((e) => e.id === id);
  if (entryIndex === -1) return entries;

  const entry = entries[entryIndex];
  const lines = entry.subtitle.split("\n");

  let firstText: string, secondText: string;

  // 行数が偶数の場合は行で分割
  if (lines.length % 2 === 0) {
    const mid = lines.length / 2;
    firstText = lines.slice(0, mid).join("\n");
    secondText = lines.slice(mid).join("\n");
  } else {
    // 奇数の場合は文字数で均等になるように分割
    const totalChars = entry.subtitle.length;
    let charCount = 0;
    let splitIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const lineLength = lines[i].length + (i < lines.length - 1 ? 1 : 0); // +1 for newline
      if (charCount + lineLength >= totalChars / 2) {
        // より均等になる方を選択
        if (
          Math.abs(charCount + lineLength - totalChars / 2) <
            Math.abs(charCount - totalChars / 2)
        ) {
          splitIndex = i + 1;
        } else {
          splitIndex = i;
        }
        break;
      }
      charCount += lineLength;
    }

    if (splitIndex === 0) splitIndex = 1;
    if (splitIndex === lines.length) splitIndex = lines.length - 1;

    firstText = lines.slice(0, splitIndex).join("\n");
    secondText = lines.slice(splitIndex).join("\n");
  }

  // 文字数の比率で時間を分割
  const totalDuration = entry.disappear - entry.appear;
  const firstCharCount = firstText.length;
  const secondCharCount = secondText.length;
  const totalChars = firstCharCount + secondCharCount;

  const firstDuration = Math.round(
    totalDuration * firstCharCount / totalChars,
  );

  const midTime = entry.appear + firstDuration;

  // 新しいIDを取得
  const maxId = entries.reduce((max, e) => Math.max(max, e.id), 0);

  const firstEntry = new SrtNode(
    entry.id,
    entry.appear,
    midTime,
    firstText,
  );
  const secondEntry = new SrtNode(
    maxId + 1,
    midTime,
    entry.disappear,
    secondText,
  );

  // エントリを置き換え
  const newEntries = [...entries];
  newEntries.splice(entryIndex, 1, firstEntry, secondEntry);

  return renumberEntries(newEntries);
}

/**
 * 現在のエントリを前のエントリと結合
 */
export function squashUpEntry(entries: SrtNode[], id: number): SrtNode[] {
  const entryIndex = entries.findIndex((e) => e.id === id);
  if (entryIndex === -1 || entryIndex === 0) return entries;

  const currentEntry = entries[entryIndex];
  const previousEntry = entries[entryIndex - 1];

  // テキストを結合
  const mergedText = previousEntry.subtitle + "\n" + currentEntry.subtitle;

  // 時間は上のエントリのappearから現在のエントリのdisappearまで
  const mergedEntry = new SrtNode(
    previousEntry.id,
    previousEntry.appear,
    currentEntry.disappear,
    mergedText,
  );

  // 新しい配列を作成
  const newEntries = [...entries];
  newEntries.splice(entryIndex - 1, 2, mergedEntry);

  return renumberEntries(newEntries);
}
